import React, { useEffect, useMemo, useRef, useState } from 'react';
import { A11y, Mousewheel, Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/a11y';
import { Swiper, SwiperSlide } from 'swiper/react';

import CustomButton from '../../common/CustomButton/CustomButton';
import { Icon } from '../../common/Icon/Icon';
import Markdown from '../../common/Markdown/Markdown';
import NoTagRenderer from '../../common/NoTagRenderer/NoTagRenderer';
import { useScreenSizeContext } from '../../core/context/screenSize.context';
import { TBackground, addBackground, slugify } from '../../core/helpers';
import './AICarousel.css';
import AICarouselItem, { IAICarouselItem } from './AICarouselItem/AICarouselItem';

interface IProps {
  title?: string;
  items: IAICarouselItem[];
  slug: string;
  background?: TBackground;
}

function AICarousel({ title, items, slug, background }: IProps) {
  const { isMobile, width } = useScreenSizeContext();
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement[] | null[]>([]);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [init, setInit] = useState(false);
  const [highestTitle, setHighestTitle] = useState(null);

  useEffect(() => {
    if (!titleRef.current.length) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const heights: any = titleRef.current.map((el) => el?.scrollHeight);
    heights.max = function () {
      return Math.max.apply(null, this);
    };
    setHighestTitle(heights.max());
  }, [width]);

  const slides = useMemo(
    () =>
      items
        ?.filter((item) => !item.mindstudio) // Mindstudio is deprecated
        ?.filter((item) => !item.hidden)
        ?.map((item, i) => (
          <SwiperSlide className="ai-carousel__slide" key={item.title}>
            <AICarouselItem
              {...item}
              ref={(el) => {
                titleRef.current[i] = el;
              }}
              highestTitle={highestTitle}
            />
          </SwiperSlide>
        )),
    [items, highestTitle],
  );

  return (
    <section
      className={`ai-carousel${background ? ' section-bg' : ' section'}`}
      {...addBackground({ img: undefined, gradient: background })}
    >
      <div className="wrap">
        <div className="ai-carousel__header gap-md">
          {!title ? null : (
            <h2 className="ai-carousel__title h2">
              <Markdown
                components={{
                  p: NoTagRenderer,
                }}
              >
                {title}
              </Markdown>
            </h2>
          )}
          <div className="ai-carousel__nav slider__nav" hidden={isLocked}>
            <CustomButton
              className="slider__arrow slider__arrow--prev"
              id={`${slug}-${slugify(title || '')}-prev`}
              type="button"
              ref={prevRef}
            >
              <span className="sr-only">Previous slide</span>
              <Icon.Chevron className="icon" />
            </CustomButton>
            <CustomButton
              className="slider__arrow slider__arrow--next"
              id={`${slug}-${slugify(title || '')}-next`}
              type="button"
              ref={nextRef}
            >
              <span className="sr-only">Next slide</span>
              <Icon.Chevron className="icon" />
            </CustomButton>
          </div>
        </div>
        <Swiper
          className={`ai-carousel__slider${isLocked ? ' locked' : ''}`}
          slidesPerView="auto"
          watchOverflow
          modules={[Navigation, Mousewheel, A11y]}
          grabCursor
          mousewheel={{
            forceToAxis: true,
          }}
          spaceBetween={isMobile ? 24 : 40}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onSwiper={() => setInit(true)}
          onLock={() => setIsLocked(true)}
          onUnlock={() => setIsLocked(false)}
        >
          {slides}
        </Swiper>
      </div>
    </section>
  );
}

export default AICarousel;
